import { registerRoute } from '../../router';
import { apiFetch, ApiError } from '../../api';
import { getUser } from '../../state';
import { escapeHtml } from '../../format';
import { openModal } from '../../components/modal';
import { parseCsv } from '../../csv';
import { extractVariables, renderTemplate } from '../../../shared/templating';
import { CHANNELS, type Channel } from '../../../shared/channels';

type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'partial' | 'failed';

interface CampaignSummary {
  id: string;
  name: string;
  channel: Channel;
  status: CampaignStatus;
  message: string;
  variables: Record<string, string>;
  scheduleAt: string | null;
  createdAt: string;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
}

interface CampaignRecipient {
  id: string;
  name: string | null;
  address: string;
  status: 'pending' | 'sent' | 'failed';
  error: string | null;
}

interface Template {
  id: string;
  name: string;
  channel: Channel;
  body: string;
  contentSid?: string;
}

const CHANNEL_LABELS: Record<Channel, string> = { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email', webhook: 'Webhook' };

const STATUS_BADGE: Record<CampaignStatus, string> = {
  draft: 'badge--muted',
  scheduled: 'badge--warning',
  sending: 'badge--warning',
  sent: 'badge--success',
  partial: 'badge--warning',
  failed: 'badge--danger',
};

let campaigns: CampaignSummary[] = [];

function role(): string | undefined {
  return getUser()?.role;
}

function canCreate(): boolean {
  return role() === 'administrator' || role() === 'manager';
}

function canSend(): boolean {
  return role() !== 'viewer';
}

async function loadCampaigns(container: HTMLElement): Promise<void> {
  campaigns = await apiFetch<CampaignSummary[]>('/api/campaigns');
  renderList(container);
}

function renderList(container: HTMLElement): void {
  const listEl = container.querySelector('#campaigns-list')!;

  if (campaigns.length === 0) {
    listEl.innerHTML = `<p style="color:var(--clr-text-muted);">No campaigns yet.</p>`;
    return;
  }

  listEl.innerHTML = campaigns
    .map((campaign) => {
      const canSendThis = canSend() && (campaign.status === 'draft' || campaign.status === 'scheduled');
      return `
      <div class="card" style="margin-bottom:12px;" data-campaign-id="${campaign.id}">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
          <div style="min-width:0;">
            <div style="font-weight:600;">${escapeHtml(campaign.name)}</div>
            <div style="display:flex;gap:8px;margin-top:6px;flex-wrap:wrap;">
              <span class="badge badge--muted">${CHANNEL_LABELS[campaign.channel]}</span>
              <span class="badge ${STATUS_BADGE[campaign.status]}">${campaign.status}</span>
            </div>
            <div style="color:var(--clr-text-muted);font-size:0.85rem;margin-top:8px;">
              ${campaign.recipientCount} recipient(s) — ${campaign.sentCount} sent, ${campaign.failedCount} failed
              ${campaign.scheduleAt ? ` — scheduled for ${escapeHtml(campaign.scheduleAt)}` : ''}
            </div>
          </div>
          <div style="display:flex;gap:8px;flex-shrink:0;">
            <button class="btn btn--ghost" data-action="toggle" data-id="${campaign.id}">Details</button>
            ${canSendThis ? `<button class="btn btn--primary" data-action="send" data-id="${campaign.id}">Send</button>` : ''}
            ${canCreate() ? `<button class="btn btn--ghost" data-action="delete" data-id="${campaign.id}">Delete</button>` : ''}
          </div>
        </div>
        <div id="recipients-${campaign.id}" class="hidden" style="margin-top:14px;border-top:1px solid var(--clr-border);padding-top:14px;"></div>
      </div>`;
    })
    .join('');

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="toggle"]').forEach((btn) => {
    btn.addEventListener('click', () => toggleRecipients(container, btn.dataset.id!));
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="send"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Send this campaign now?')) return;
      btn.disabled = true;
      try {
        const result = await apiFetch<{ sent: number; failed: number; error?: string }>(`/api/campaigns/${btn.dataset.id}/send`, {
          method: 'POST',
        });
        alert(result.error ? `Could not send: ${result.error}` : `Sent ${result.sent}, failed ${result.failed}.`);
      } catch (err) {
        alert(err instanceof ApiError ? err.message : 'Something went wrong.');
      } finally {
        await loadCampaigns(container);
      }
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this campaign?')) return;
      await apiFetch(`/api/campaigns/${btn.dataset.id}`, { method: 'DELETE' });
      await loadCampaigns(container);
    });
  });
}

async function toggleRecipients(container: HTMLElement, campaignId: string): Promise<void> {
  const panel = container.querySelector(`#recipients-${campaignId}`)!;
  if (!panel.classList.contains('hidden')) {
    panel.classList.add('hidden');
    return;
  }

  panel.classList.remove('hidden');
  panel.textContent = 'Loading…';

  try {
    const detail = await apiFetch<CampaignSummary & { recipients: CampaignRecipient[] }>(`/api/campaigns/${campaignId}`);
    panel.innerHTML = detail.recipients
      .map(
        (r) => `
      <div style="display:flex;justify-content:space-between;font-size:0.85rem;padding:4px 0;">
        <span>${escapeHtml(r.name ?? '')} ${escapeHtml(r.address)}</span>
        <span class="badge ${r.status === 'sent' ? 'badge--success' : r.status === 'failed' ? 'badge--danger' : 'badge--muted'}">${r.status}</span>
      </div>`,
      )
      .join('');
  } catch {
    panel.textContent = 'Could not load recipients.';
  }
}

function openCampaignWizard(container: HTMLElement): void {
  const channelOptions = CHANNELS.map((ch) => `<option value="${ch}">${CHANNEL_LABELS[ch]}</option>`).join('');

  const { root, close } = openModal(
    'New campaign',
    `
    <form id="campaign-form">
      <div class="field">
        <label for="campaign-name">Campaign name</label>
        <input class="input" id="campaign-name" required>
      </div>
      <div class="field">
        <label for="campaign-channel">Channel</label>
        <select class="input" id="campaign-channel">${channelOptions}</select>
      </div>

      <div class="field">
        <label>Recipients</label>
        <div style="display:flex;gap:14px;font-size:0.85rem;margin-bottom:10px;">
          <label><input type="radio" name="recipient-mode" value="single" checked> Single</label>
          <label><input type="radio" name="recipient-mode" value="manual"> Manual list</label>
          <label><input type="radio" name="recipient-mode" value="csv"> CSV upload</label>
        </div>
        <div id="recipient-single">
          <input class="input" id="recipient-single-address" placeholder="Phone or email" style="margin-bottom:8px;">
          <input class="input" id="recipient-single-name" placeholder="Name (optional)">
        </div>
        <textarea class="input hidden" id="recipient-manual" placeholder="One address per line"></textarea>
        <input class="input hidden" id="recipient-csv" type="file" accept=".csv,text/csv">
        <p id="recipient-csv-preview" style="font-size:0.8rem;color:var(--clr-text-muted);margin-top:6px;"></p>
      </div>

      <div class="field">
        <label for="campaign-template">Start from a template (optional)</label>
        <select class="input" id="campaign-template"><option value="">— None —</option></select>
      </div>
      <div class="field">
        <label for="campaign-message">Message</label>
        <textarea class="input" id="campaign-message" required placeholder="Hi {{name}}, ..."></textarea>
      </div>

      <div id="campaign-variable-fields"></div>
      <div class="field">
        <label>Preview</label>
        <p id="campaign-preview" style="font-size:0.85rem;background:var(--clr-bg2);border-radius:var(--radius);padding:10px;white-space:pre-wrap;"></p>
      </div>

      <div id="content-sid-wrapper" class="field" style="display:none;">
        <label for="campaign-content-sid">Content Template SID <span style="font-weight:400;color:var(--clr-text-muted);">(WhatsApp — HXxxx…)</span></label>
        <input class="input" id="campaign-content-sid" placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx">
        <p style="font-size:0.78rem;color:var(--clr-text-muted);margin-top:4px;">Required for business-initiated WhatsApp messages. Variables map to {{1}}, {{2}}, … in the Twilio template in order of appearance.</p>
      </div>

      <div class="field">
        <label for="campaign-schedule">Schedule for later (optional)</label>
        <input class="input" id="campaign-schedule" type="datetime-local">
        <p style="font-size:0.78rem;color:var(--clr-text-muted);margin-top:4px;">
          Scheduled campaigns are saved but must be sent manually from the list — automatic scheduled sending isn't wired up yet.
        </p>
      </div>

      <p class="error-text" id="campaign-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="campaign-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary" id="campaign-submit">Send</button>
      </div>
    </form>
  `,
  );

  // Recipient mode switching
  const singleEl = root.querySelector<HTMLElement>('#recipient-single')!;
  const manualEl = root.querySelector<HTMLTextAreaElement>('#recipient-manual')!;
  const csvEl = root.querySelector<HTMLInputElement>('#recipient-csv')!;
  let csvRows: Record<string, string>[] = [];

  root.querySelectorAll<HTMLInputElement>('input[name="recipient-mode"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      singleEl.classList.toggle('hidden', radio.value !== 'single' || !radio.checked);
      manualEl.classList.toggle('hidden', radio.value !== 'manual' || !radio.checked);
      csvEl.classList.toggle('hidden', radio.value !== 'csv' || !radio.checked);
    });
  });

  csvEl.addEventListener('change', async () => {
    const file = csvEl.files?.[0];
    if (!file) return;
    csvRows = parseCsv(await file.text());
    root.querySelector('#recipient-csv-preview')!.textContent = `${csvRows.length} row(s) found.`;
  });

  // Template picker
  const templateSelect = root.querySelector<HTMLSelectElement>('#campaign-template')!;
  const channelSelect = root.querySelector<HTMLSelectElement>('#campaign-channel')!;
  const messageField = root.querySelector<HTMLTextAreaElement>('#campaign-message')!;
  const scheduleField = root.querySelector<HTMLInputElement>('#campaign-schedule')!;
  const submitBtn = root.querySelector<HTMLButtonElement>('#campaign-submit')!;
  const contentSidWrapper = root.querySelector<HTMLElement>('#content-sid-wrapper')!;
  const contentSidInput = root.querySelector<HTMLInputElement>('#campaign-content-sid')!;
  let templates: Template[] = [];

  function syncContentSidVisibility(): void {
    contentSidWrapper.style.display = channelSelect.value === 'whatsapp' ? '' : 'none';
    if (channelSelect.value !== 'whatsapp') contentSidInput.value = '';
  }

  function refreshTemplateOptions(): void {
    const matching = templates.filter((t) => t.channel === channelSelect.value);
    templateSelect.innerHTML = '<option value="">— None —</option>' + matching.map((t) => `<option value="${t.id}">${escapeHtml(t.name)}</option>`).join('');
    syncContentSidVisibility();
  }

  apiFetch<Template[]>('/api/templates')
    .then((list) => {
      templates = list;
      refreshTemplateOptions();
    })
    .catch(() => undefined);

  channelSelect.addEventListener('change', refreshTemplateOptions);
  templateSelect.addEventListener('change', () => {
    const chosen = templates.find((t) => t.id === templateSelect.value);
    if (chosen) {
      messageField.value = chosen.body;
      if (chosen.contentSid) contentSidInput.value = chosen.contentSid;
      refreshVariableFields();
    }
  });

  scheduleField.addEventListener('change', () => {
    submitBtn.textContent = scheduleField.value ? 'Schedule' : 'Send';
  });

  // Variable detection + preview (excludes name/date — those auto-resolve per recipient/today)
  function refreshVariableFields(): void {
    const variables = extractVariables(messageField.value).filter((v) => v !== 'name' && v !== 'date');
    const fieldsEl = root.querySelector('#campaign-variable-fields')!;
    fieldsEl.innerHTML = variables
      .map(
        (v) => `
      <div class="field" style="margin-bottom:10px;">
        <label for="var-${v}">{{${escapeHtml(v)}}} (applies to all recipients)</label>
        <input class="input" id="var-${v}" data-var="${v}">
      </div>`,
      )
      .join('');
    fieldsEl.querySelectorAll<HTMLInputElement>('[data-var]').forEach((input) => input.addEventListener('input', updatePreview));
    updatePreview();
  }

  function updatePreview(): void {
    const values: Record<string, string> = { date: new Date().toISOString().slice(0, 10), name: 'Sample Name' };
    root.querySelectorAll<HTMLInputElement>('#campaign-variable-fields [data-var]').forEach((input) => {
      if (input.value.trim()) values[input.dataset.var!] = input.value.trim();
    });
    root.querySelector('#campaign-preview')!.textContent = renderTemplate(messageField.value, values) || '(empty)';
  }

  messageField.addEventListener('input', refreshVariableFields);
  refreshVariableFields();

  root.querySelector('#campaign-cancel')?.addEventListener('click', close);

  root.querySelector('#campaign-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#campaign-error')!;
    errorEl.textContent = '';

    const mode = root.querySelector<HTMLInputElement>('input[name="recipient-mode"]:checked')!.value;
    let recipients: { name?: string | null; address: string }[] = [];

    if (mode === 'single') {
      const address = (root.querySelector('#recipient-single-address') as HTMLInputElement).value.trim();
      const name = (root.querySelector('#recipient-single-name') as HTMLInputElement).value.trim();
      if (!address) {
        errorEl.textContent = 'Enter a recipient address.';
        return;
      }
      recipients = [{ address, name: name || null }];
    } else if (mode === 'manual') {
      recipients = manualEl.value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((address) => ({ address }));
    } else {
      recipients = csvRows
        .map((row) => ({ address: row.address || row.phone || row.email || '', name: row.name || null }))
        .filter((r) => r.address);
    }

    if (recipients.length === 0) {
      errorEl.textContent = 'Add at least one recipient.';
      return;
    }

    const variables: Record<string, string> = {};
    root.querySelectorAll<HTMLInputElement>('#campaign-variable-fields [data-var]').forEach((input) => {
      if (input.value.trim()) variables[input.dataset.var!] = input.value.trim();
    });

    const contentSid = contentSidInput.value.trim() || undefined;
    const payload = {
      name: (root.querySelector('#campaign-name') as HTMLInputElement).value.trim(),
      channel: channelSelect.value as Channel,
      message: messageField.value.trim(),
      variables,
      ...(contentSid ? { contentSid } : {}),
      scheduleAt: scheduleField.value || null,
      recipients,
    };

    try {
      const created = await apiFetch<{ id: string }>('/api/campaigns', { method: 'POST', body: JSON.stringify(payload) });

      if (!payload.scheduleAt) {
        const result = await apiFetch<{ sent: number; failed: number; error?: string }>(`/api/campaigns/${created.id}/send`, {
          method: 'POST',
        });
        close();
        alert(result.error ? `Saved, but could not send: ${result.error}` : `Sent ${result.sent}, failed ${result.failed}.`);
      } else {
        close();
        alert('Campaign scheduled. Send it manually from the list when ready.');
      }

      await loadCampaigns(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/campaigns',
  title: 'Campaigns',
  render: ({ container }) => {
    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>Campaigns</h1>
          <p>Send a message to one recipient, a list, or a CSV upload — provider details stay behind the scenes.</p>
        </div>
        ${canCreate() ? '<button class="btn btn--primary" id="new-campaign-btn">+ New Campaign</button>' : ''}
      </div>
      <div id="campaigns-list">Loading…</div>
    `;

    container.querySelector('#new-campaign-btn')?.addEventListener('click', () => openCampaignWizard(container));

    loadCampaigns(container).catch(() => {
      container.querySelector('#campaigns-list')!.textContent = 'Could not load campaigns.';
    });
  },
});
